import { useMemo } from "react";
import { MessageCircle, Zap, PlayCircle, ShieldCheck, Sparkles, ExternalLink } from "lucide-react";

// Two curated briefing videos — chosen by niche. Same URLs used in the
// Alfred Showreel tab so leaders and prospects see the same asset.
const BRIEFING_VIDEOS = {
  campground: {
    label: "Bentley · Adventure Captain Hero",
    src: "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/31481dff275ace52.mp4",
  },
  default: {
    label: "The 7-Hour Handshake · Onboarding",
    src: "https://customer-assets.emergentagent.com/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/cb8b1baec5734ceaad1fe5620c0f76af_output.mp4",
  },
};

const WHATSAPP_HREF =
  "https://wa.me/15678761419?text=I%20just%20finished%20my%20AI%20Utility%20Assessment%20and%20I%27m%20ready%20to%20plug%20into%20the%20Grid.";

// Extract a bare host (or best-guess) from whatever the user typed in
// "Business Name / Website" — accepts full URLs, "wexlerauto.com", or free-text.
function extractWebsite(input) {
  if (!input) return "";
  const s = String(input).trim();
  // If it already looks like a URL or a domain, keep just the host+path.
  const urlMatch = s.match(/([a-z0-9-]+\.[a-z]{2,}(?:\/[^\s]*)?)/i);
  return urlMatch ? urlMatch[1] : "";
}

// Best-guess business name — strip any trailing URL / website chunk so the
// demo page hero doesn't render "Wexler & Co. — wexlerauto.com" verbatim.
function extractBusinessName(input) {
  if (!input) return "";
  const s = String(input).trim();
  // Split on em-dash / hyphen with space around it, or " · " separators.
  const parts = s.split(/\s*[—–\-·|]\s*/);
  const first = parts[0].trim();
  return first || s;
}

function buildDemoUrl({ businessName, contact, niche }) {
  const nameOnly = extractBusinessName(businessName);
  const site = extractWebsite(businessName) || extractWebsite(contact);
  const params = new URLSearchParams();
  if (nameOnly) params.set("name", nameOnly);
  if (site) params.set("website", site);
  const n = String(niche || "").toLowerCase();
  if (n.includes("camp") || n === "campground") params.set("variant", "campground");
  return `/demo?${params.toString()}`;
}

function pickVideo(niche) {
  const n = String(niche || "").toLowerCase();
  if (n.includes("camp") || n.includes("rv") || n === "campground") {
    return BRIEFING_VIDEOS.campground;
  }
  return BRIEFING_VIDEOS.default;
}

/**
 * Grid Activation Bridge — replaces the flat "we will contact you" dead zone.
 * Shown at the tail of /local's ResultPanel and inside /demo's PilotPagePreview
 * once a partial lead has been captured. Bridges the wait with:
 *   1. Readiness Gauge (personalized from the Business Snapshot answers)
 *   2. Niche-appropriate briefing video
 *   3. Instant WhatsApp CTA to the Lead Architect
 *
 * `readiness` (optional) shape: { score:number, label:string, factors:[
 *   { id, label, delta, polarity } ] }
 * When absent (e.g. cold /demo traffic with no assessment), falls back to a
 * neutral 45 baseline — never a random value.
 */
export default function GridActivationBridge({
  niche = "default",
  businessName = "",
  contact = "",
  leadId = "",
  readiness = null,
}) {
  const video = useMemo(() => pickVideo(niche), [niche]);
  const gauge = useMemo(() => {
    if (readiness && typeof readiness.score === "number") {
      return {
        score: readiness.score,
        label: readiness.label || defaultLabel(readiness.score),
        factors: readiness.factors || [],
        personalized: true,
      };
    }
    // No snapshot available — show a fixed baseline instead of a fake random.
    return {
      score: 45,
      label: "Baseline scan · complete the assessment for your personalized score",
      factors: [],
      personalized: false,
    };
  }, [readiness]);

  return (
    <section
      className="glass-strong rounded-sm p-6 sm:p-8 mt-6 relative overflow-hidden"
      data-testid="grid-activation-bridge"
      style={{ borderTop: "1px solid rgba(212,175,55,0.4)" }}
    >
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(212,175,55,0.9), transparent)",
        }}
      />

      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.34em] text-gold mb-3">
        <Zap size={11} /> Last 50 Mile · Grid Activation Bridge
      </div>
      <h2 className="font-display text-2xl sm:text-3xl text-white leading-tight">
        You&apos;re on the ramp.
        {businessName && (
          <>
            {" "}
            <span className="text-gold italic">{businessName}</span> is queued for
            connection.
          </>
        )}
      </h2>
      <p className="text-slate-400 text-sm mt-2 max-w-2xl">
        John and Parker are being paged now. While the transfer completes,
        here&apos;s where your grid stands and what your onboarding will feel like.
      </p>

      {/* Two-column layout on desktop: gauge + video */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-5">
        <ReadinessGauge
          score={gauge.score}
          label={gauge.label}
          factors={gauge.factors}
          personalized={gauge.personalized}
        />
        <BriefingVideo video={video} niche={niche} />
      </div>

      {/* Primary CTA — Free custom demo + e-book (matches the exit-intent bribe) */}
      <div className="mt-6">
        <a
          href={buildDemoUrl({ businessName, contact, niche })}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-gold w-full sm:w-auto sm:inline-flex px-6 py-4 rounded-sm text-sm sm:text-base font-display uppercase tracking-[0.16em] flex items-center justify-center gap-3 min-h-[56px]"
          data-testid="grid-primary-cta"
        >
          <Sparkles size={18} /> View My Free Custom Demo &amp; E-Book
          <ExternalLink size={14} className="opacity-70" />
        </a>
        <p
          className="mt-3 text-sm text-slate-300 leading-relaxed max-w-2xl"
          data-testid="grid-primary-subtext"
        >
          Alfred has built your custom foyer. Click above to experience your
          business on the Grid. We&apos;ll also email your free{" "}
          <span className="text-gold font-display">2026 Growth Roadmap</span>{" "}
          within <span className="text-gold">10 minutes</span>.
        </p>
      </div>

      {/* Secondary — "human" WhatsApp escape hatch, demoted to a small link */}
      <div className="mt-5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500 font-mono-aviation">
        <ShieldCheck size={11} className="text-emerald-400" />
        <span className="uppercase tracking-[0.22em] text-emerald-400">
          Need Help?
        </span>
        <a
          href={WHATSAPP_HREF}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-slate-300 hover:text-gold transition-colors border-b border-dashed border-slate-600 hover:border-gold pb-0.5"
          data-testid="grid-whatsapp-button"
        >
          <MessageCircle size={11} /> Chat with a human
        </a>
        <span className="text-slate-600">·</span>
        <span>John &amp; Parker on WhatsApp</span>
      </div>

      {leadId && (
        <div className="mt-5 text-[10px] font-mono-aviation text-slate-500 break-all">
          Grid Ref · {leadId}
        </div>
      )}
    </section>
  );
}

// ------- Readiness Gauge -------
function defaultLabel(score) {
  if (score < 35) return "Grid Offline — Critical Rebuild Required.";
  if (score < 55) return "Grid Weak — Major Optimization Needed.";
  if (score < 75) return "Grid Stable — High-Leverage Gains Available.";
  return "Grid Strong — Ready to Scale Aggressively.";
}

function ReadinessGauge({ score, label, factors = [], personalized = false }) {
  const radius = 68;
  const stroke = 10;
  const c = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, score));
  const offset = c * (1 - clamped / 100);
  const color =
    clamped < 40 ? "#F43F5E" : clamped < 55 ? "#F5C842" : "#34D399";

  return (
    <div
      className="lg:col-span-2 rounded-sm border p-5 flex flex-col items-center text-center"
      style={{
        borderColor: `${color}55`,
        background:
          "linear-gradient(180deg, rgba(6,11,20,0.7) 0%, rgba(6,11,20,0.4) 100%)",
      }}
      data-testid="grid-readiness-gauge"
    >
      <div className="text-[10px] uppercase tracking-[0.32em] text-slate-500 mb-3">
        Grid Readiness
      </div>
      <svg width="160" height="160" viewBox="0 0 160 160" className="shrink-0">
        <circle
          cx="80"
          cy="80"
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={stroke}
        />
        <circle
          cx="80"
          cy="80"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform="rotate(-90 80 80)"
          style={{ transition: "stroke-dashoffset 1.2s ease-out" }}
        />
        <text
          x="80"
          y="76"
          textAnchor="middle"
          fill="#ffffff"
          fontSize="34"
          fontFamily="Outfit, sans-serif"
          fontWeight="300"
          data-testid="grid-readiness-score"
        >
          {clamped}%
        </text>
        <text
          x="80"
          y="96"
          textAnchor="middle"
          fill={color}
          fontSize="9"
          letterSpacing="3"
          fontFamily="Manrope, sans-serif"
        >
          READINESS
        </text>
      </svg>
      <div
        className="mt-3 text-[13px] font-display leading-snug"
        style={{ color }}
        data-testid="grid-readiness-label"
      >
        {label || defaultLabel(clamped)}
      </div>

      {personalized && factors.length > 0 ? (
        <div className="w-full mt-4 border-t border-white/5 pt-3" data-testid="grid-readiness-factors">
          <div className="text-[9px] uppercase tracking-[0.28em] text-slate-500 mb-2">
            Score Breakdown
          </div>
          <ul className="space-y-1 text-left">
            {factors.map((f) => {
              const isPos = f.polarity === "pos" || f.delta > 0;
              const c2 = isPos ? "#34D399" : "#F43F5E";
              return (
                <li
                  key={f.id}
                  className="flex items-start justify-between gap-3 text-[11px] leading-tight"
                  data-testid={`grid-readiness-factor-${f.id}`}
                >
                  <span className="text-slate-300 flex-1">{f.label}</span>
                  <span className="font-mono-aviation shrink-0" style={{ color: c2 }}>
                    {f.delta > 0 ? `+${f.delta}` : f.delta}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <div className="text-[10px] text-slate-500 mt-1 font-mono-aviation">
          Baseline scan · full audit after onboarding
        </div>
      )}
    </div>
  );
}

// ------- Briefing Video -------
function BriefingVideo({ video, niche }) {
  return (
    <div
      className="lg:col-span-3 rounded-sm border overflow-hidden"
      style={{
        borderColor: "rgba(212,175,55,0.35)",
        background: "rgba(6,11,20,0.6)",
        boxShadow: "0 0 32px -18px rgba(212,175,55,0.6)",
      }}
      data-testid="grid-briefing-video-frame"
    >
      <div className="px-4 pt-4 pb-3 flex items-center gap-2">
        <PlayCircle size={14} className="text-gold" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-gold">
            Watch Your Grid Briefing While You Wait
          </div>
          <div className="font-display text-sm text-white mt-0.5" data-testid="grid-briefing-video-label">
            {video.label}
          </div>
        </div>
      </div>
      <video
        key={video.src}
        src={video.src}
        controls
        playsInline
        preload="metadata"
        className="w-full aspect-video bg-black"
        data-testid="grid-briefing-video"
      />
      <div className="px-4 py-2 text-[10px] font-mono-aviation text-slate-500">
        Niche · {niche || "default"}
      </div>
    </div>
  );
}
